import { ForwardedRef, forwardRef, useCallback, useEffect, useState } from "react";
import { SelectInstance } from "react-select";
import { DataRow, Spinner } from "../../general";
import { Shelf } from "../../layout";
import { debounce } from "../../utilities";
import { Dropdown } from "../Dropdown";
import { OptionItem } from "../Dropdown/OptionItem";
import { SharedDropdown } from "../Dropdown/SharedDropdown";
import { DataDropdownProps } from "./DataDropdown";
import { useConsolidatedRows } from "./useConsolidatedRows";

export interface SharedDataDropdownProps extends DataDropdownProps<object> {
  /**
   * The input ID.
   */
  readonly id: string;

  /**
   * Indicates whether the input is required.
   */
  readonly required?: boolean;
}

export const SharedDataDropdown = forwardRef(function SharedDataDropdown(
  props: SharedDataDropdownProps,
  ref: ForwardedRef<SelectInstance<OptionItem | null, boolean>>,
) {
  const { dataSource, optionLabelProvider, defaultValue = null, searchField, suffix, onChange, onSearchChange, autoComplete = "off" } = props;
  const { value = defaultValue } = props;

  const [selectedRow, setSelectedRow] = useState<DataRow<object> | null>(null);

  // We mandate that the data source must at minimum have 10 items per page to ensure scrolling works, etc.
  useEffect(() => {
    if (dataSource.pageSize < 10) dataSource.setPageSize(10);
  }, [dataSource.pageSize]);

  const rowState = useConsolidatedRows(dataSource);

  useEffect(() => {
    const doRowValueLoad = async () => {
      if (value !== null) {
        const row = selectedRow && selectedRow.id === value ? selectedRow : await dataSource.get(value);
        if (!row) onChange?.(null, value);
        else setSelectedRow((current) => (!current || current.id !== value ? row : current));
      } else setSelectedRow(null);
    };

    if (dataSource.busy || !dataSource.enabled) return;

    doRowValueLoad();
  }, [dataSource.get, dataSource.busy, dataSource.enabled, value]);

  const searchChangeHandler = useCallback(
    debounce((value: string | null, prev: string | null) => {
      if (searchField && !value) dataSource.filterBy(searchField, null);
      else if (searchField) dataSource.filterBy(searchField, value);
      onSearchChange?.(value, prev);
    }, 500),
    [dataSource, searchField],
  );

  const scrollHandler = useCallback(() => {
    if (dataSource.hasNextPage) dataSource.next();
  }, [dataSource]);

  const rowElements = rowState.rows.map(({ id, data }) => {
    return (
      <Dropdown.Option key={id} value={id}>
        {optionLabelProvider(data)}
      </Dropdown.Option>
    );
  });

  const items = selectedRow && value !== null && !rowState.indices[value] ? <Dropdown.Group>{rowElements}</Dropdown.Group> : rowElements;

  return (
    <SharedDropdown
      ref={ref}
      {...props}
      autoComplete={autoComplete}
      searchable={!!searchField}
      manualSearchFilter
      readOnly={props.readOnly}
      disabled={dataSource.status === "error" ? true : props.disabled}
      suffix={
        (dataSource.busy) ? (
          <div role="status" aria-live="polite" aria-busy={dataSource.busy}>
            <Shelf>
              {suffix}
              <Spinner size="regular" aria-label="Loading options" />
            </Shelf>
          </div>) : suffix
      }
      onSearchChange={searchChangeHandler}
      onMenuScrollToBottom={scrollHandler}
    >
      {selectedRow && value !== null && !rowState.indices[value] && (
        <Dropdown.Option value={value}>{optionLabelProvider(selectedRow.data)}</Dropdown.Option>
      )}
      {items}
    </SharedDropdown>
  );
});
